import { useActivateDealMutation } from '@src/components/OMS/ManualDealTradeView/endpoints/activateDealEndpoint';
import { manualDealSliceActions } from '@src/components/OMS/ManualDealTradeView/ManualDealSlice';
import { openManualDealTradeFormThunk } from '@src/components/OMS/ManualDealTradeView/thunks/openManualDealTradeFormThunk';
import { ModifyDealWarningDialog } from '@src/containers/Blotters/Orders/Dialogs/ModifyDealWarningDialog';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import { useAppStateDispatch } from '@src/providers/AppStateProvider/AppStateProviderHooks';
import type { OTCDealData } from '@src/types/OTC';
import { isFixedRateLoanDeal, isFloatingRateLoanDeal } from '@src/types/OTC/otcGuards';
import {
  notifyIfDealSecurityInvalidForModify,
  notifyIfProductTemplateMissingForModify,
} from '@src/utils/OTC/notifyModifyDealGuardFailures';
import { getOtcDealDisplayUser } from '@src/utils/OTC/otcDealDisplayUtils';
import { isDealActivatable, isDealCancelable, isDealModifiable } from '@src/utils/OTC/otcUtils';
import { validateInstantiatedDealSecurityForModify } from '@src/utils/OTC/validateInstantiatedDealSecurityForModify';
import type { OTCProductTemplateSecurity } from '@talos/kyoko';
import {
  ACTION,
  filterByCellValueMenuItem,
  getShowJSONContextItem,
  IconName,
  useCallbackRef,
  useDisclosureWithContext,
  useGetDefaultContextMenuItems,
  useGlobalToasts,
  useJsonModal,
  useMixpanel,
  useSecuritiesContext,
  useUserContext,
  type AgGridIconButtonProps,
  type Column,
  type ColumnDef,
  type FilterableProperty,
  type FilterBuilderSide,
  type Security,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useCancelDealDialog } from '../CancelDealDialog';
import { getProductTemplateForDeal } from '../InstrumentInspectorDrawer/shared/getProductTemplateForDeal';
import { createDealJsonObject } from '../useCreateDealJsonObject';
import { LoanGridRowMenu } from './LoanGridRowMenu';
import { colIDToFilterBuilderKey } from './useLoanFilter';
interface UseLoanMenuParams {
  openClause?: (property: string | undefined, side?: FilterBuilderSide, value?: string | string[]) => void;
  filterableProperties?: FilterableProperty<string>[];
  /** Opens loan instrument inspector; must be stable (e.g. from parent `useCallback`). */
  openLoanInstrumentInspectorDrawer: (deal: OTCDealData, onlyIfOpen?: boolean) => void;
}

const { modifyDeal } = manualDealSliceActions;

export function useLoanMenu({
  openClause,
  filterableProperties,
  openLoanInstrumentInspectorDrawer,
}: UseLoanMenuParams): {
  columns: Column[];
  getContextMenuItems: (params: GetContextMenuItemsParams) => (MenuItemDef | string)[];
  dialogs: ReactNode;
} {
  const dispatch = useAppStateDispatch();
  const securitiesContext = useSecuritiesContext();
  const { securitiesBySymbol } = securitiesContext;
  const { add: addToast } = useGlobalToasts();

  const onModifyDeal = useCallback(
    (deal: OTCDealData) => {
      const dealSecurity = validateInstantiatedDealSecurityForModify(deal, securitiesBySymbol);
      if (!notifyIfDealSecurityInvalidForModify(deal, dealSecurity, addToast)) {
        return;
      }

      const productTemplate = getProductTemplateForDeal(deal, securitiesContext) as
        | OTCProductTemplateSecurity
        | undefined;
      if (!notifyIfProductTemplateMissingForModify(deal, productTemplate, addToast)) {
        return;
      }

      dispatch(modifyDeal({ deal, productTemplate }));
      // Thunk will detect deal being modified and use its booking type (not the hardcoded 'fixedRateLoan')
      dispatch(openManualDealTradeFormThunk({}));
    },
    [dispatch, securitiesBySymbol, securitiesContext, addToast]
  );
  const { handleClickJson, jsonModal } = useJsonModal<{ loan: OTCDealData; security: Security | undefined }>();
  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();
  const { user } = useUserContext();
  const modifyDealWarningDialog = useDisclosureWithContext<OTCDealData>({});
  const handleCancelDeal = useCancelDealDialog();
  const [activateDeal] = useActivateDealMutation();
  const mixpanel = useMixpanel();
  const { isAuthorized } = useRoleAuth();
  const canSubmitDeal = isAuthorized(ACTION.SUBMIT_DEAL);
  const canCancelDeal = isAuthorized(ACTION.CANCEL_DEAL);

  const createCustomJsonObject = useCallbackRef((loan?: OTCDealData) => {
    createDealJsonObject({
      deal: loan,
      propertyName: 'loan',
      securitiesBySymbol,
      handleClickJson,
      mixpanel,
    });
  });

  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<OTCDealData>) => {
    const data = params.node?.data;

    const isModifiable = data && isDealModifiable(data);
    const isCancelable = data && isDealCancelable(data);
    const canModifyLoan = data && isModifiable && (isFixedRateLoanDeal(data) || isFloatingRateLoanDeal(data));
    const canActivateLoan = data && isDealActivatable(data) && data.ID != null;

    const menuItems = compact([
      ...(openClause
        ? filterByCellValueMenuItem({
            params,
            filterableProperties: filterableProperties || [],
            openClause,
            colIDToFilterBuilderKey,
            mixpanel,
          })
        : []),
      'separator',
      {
        name: 'View Instrument Details',
        action: () => {
          if (data) {
            openLoanInstrumentInspectorDrawer(data);
          }
        },
        icon: `<i class="ag-icon ${IconName.InformationCircle}"/>`,
      },
    ]);

    const secondaryActions: MenuItemDef[] = [];
    if (canModifyLoan && canSubmitDeal) {
      secondaryActions.push({
        name: 'Modify Loan',
        action: () => {
          if (data) {
            if (getOtcDealDisplayUser(data) === user.Name) {
              onModifyDeal(data);
            } else {
              modifyDealWarningDialog.open(data);
            }
          }
        },
        icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
      });
    }
    if (isCancelable && canCancelDeal) {
      secondaryActions.push({
        name: 'Cancel Loan',
        action: () => {
          handleCancelDeal(data);
        },
        icon: `<i class="ag-icon ${IconName.Trash}"/>`,
      });
    }
    if (canActivateLoan && canSubmitDeal) {
      secondaryActions.push({
        name: 'Activate Loan',
        action: () => {
          if (data?.ID != null) {
            void activateDeal({ dealId: data.ID });
          }
        },
        icon: `<i class="ag-icon ${IconName.CheckCircle}"/>`,
      });
    }
    if (secondaryActions.length > 0) {
      menuItems.push('separator', ...secondaryActions);
    }

    menuItems.push(
      'separator',
      getShowJSONContextItem({
        params,
        handleClickJson: createCustomJsonObject,
      }),
      'separator',
      ...getDefaultContextMenuItems(params)
    );

    return menuItems;
  });

  const columns = useMemo(
    () =>
      [
        // Show More Details button (pinned to the right, before hamburger menu)
        {
          type: 'iconButton',
          id: 'show-more-details',
          frozen: true,
          pinned: 'right',
          suppressColumnsToolPanel: true,
          width: 40,
          params: {
            icon: IconName.Deepdive,
            onClick: params => {
              const data: OTCDealData | undefined = params.node.data;
              if (data) {
                openLoanInstrumentInspectorDrawer(data);
              }
            },
          } satisfies AgGridIconButtonProps,
        },
        {
          type: 'hamburgerMenu',
          id: 'loanMenu',
          width: 40,
          params: {
            renderItems: params => <LoanGridRowMenu {...params} onShowJson={createCustomJsonObject} />,
          },
        },
      ] satisfies ColumnDef<OTCDealData>[],
    [createCustomJsonObject, openLoanInstrumentInspectorDrawer]
  );

  const dialogs = useMemo(
    () => (
      <>
        {jsonModal}
        <ModifyDealWarningDialog
          disclosure={modifyDealWarningDialog}
          handleConfirm={deal => {
            onModifyDeal(deal);
          }}
        />
      </>
    ),
    [jsonModal, modifyDealWarningDialog, onModifyDeal]
  );
  return {
    columns,
    getContextMenuItems,
    dialogs,
  };
}
